import type { LayerState, ServiceLayers } from '@takeshape/schema';
import type { SagaIterator } from 'redux-saga';
import { all, call, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { SET_CURRENT_PROJECT } from '../../project/action-types';
import { getCurrentProjectId } from '../../project/selectors';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from '../constants';
import { getBranchByName } from '../selectors';
import { useWorkingCopyStore } from '../store/working-copy-store';
import type { Branch } from '../types';
import { fetchServiceLayersRoutine, servicesHaventChanged } from './fetch-service-layers';
import { postSchemaRoutine } from './post-schema';
import { updateSchema } from './update-schema';

export function layerStateToServiceLayers(layerState: LayerState[]): ServiceLayers {
  return layerState.reduce((acc, layer) => {
    acc[layer.id] = layer;
    return acc;
  }, {} as ServiceLayers);
}

export function* handleUpdateAfterDeploy({ meta }: ActionType<typeof postSchemaRoutine.fulfilled>): SagaIterator {
  if (meta.branch !== SCRATCH_BRANCH) {
    yield call(useWorkingCopyStore.getState().updateAfterDeploy);
  }
}

export function* handleUpdateSchemaFulfilled(_action: ActionType<typeof updateSchema.fulfilled>): SagaIterator {
  yield call(useWorkingCopyStore.getState().updateAfterDeploy);
}

export function* handleFetchServiceLayersFulfilled({
  payload,
  meta
}: ActionType<typeof fetchServiceLayersRoutine.fulfilled>): SagaIterator {
  if (meta.branch === SCRATCH_BRANCH) {
    yield call(
      useWorkingCopyStore.getState().setServiceLayers,
      layerStateToServiceLayers(payload.getAllServiceLayers.layers)
    );
  }
}

/**
 * Normally the runtime schema is built when the service layers are fetched.
 * If we are skipping the fetch because the service layers haven't changed,
 * we should still update the runtime schema.
 */
export function* buildRuntimeSchemaIfServicesUnchanged(
  action: ActionType<typeof fetchServiceLayersRoutine.requested>
): SagaIterator {
  const { branch = DEFAULT_BRANCH } = action.meta;
  const projectId = yield select(getCurrentProjectId);
  if (!projectId) {
    return null;
  }

  const branchState: Branch | undefined = yield select(getBranchByName, branch);

  if (branchState?.serviceLayers.loading) {
    return;
  }

  if (servicesHaventChanged(branchState)) {
    useWorkingCopyStore.getState().updateSchemaModels();
  }
}

export function* handleSetCurrentProject(): SagaIterator {
  yield call(useWorkingCopyStore.getState().reset);
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(postSchemaRoutine.fulfilled, handleUpdateAfterDeploy),
    takeLatest(updateSchema.fulfilled, handleUpdateSchemaFulfilled),
    takeLatest(fetchServiceLayersRoutine.fulfilled, handleFetchServiceLayersFulfilled),
    takeEvery(fetchServiceLayersRoutine.requested, buildRuntimeSchemaIfServicesUnchanged),
    takeLatest(SET_CURRENT_PROJECT, handleSetCurrentProject)
  ]);
}
